// Ecosystem Edge §04 consumption — archetype → Home section ordering
// (decision-as-code, pure, no I/O — mirrors archetypeSurface.ts).
//
// The Home page has five prototype-faithful sections. This module
// decides their order/visibility from the resolved ArchetypeSurface.
// HONEST DEFAULT: the neutral DEFAULT_SURFACE (no archetype detected
// yet) MUST yield the exact canonical prototype order — zero
// behavioural change for new/unprofiled users (no fabricated
// personalisation before the profile exists).
//
// Identity sections are fixed: "hero" + "stats" always lead (brand /
// at-a-glance), "activity" (the auditable trail) always closes. Only
// the two work modules — "grid" (Action Queue + Goal) and "signals"
// (Signals Preview) — are reordered, and "signals" is dropped for the
// minimal (Casual / intelligence_learner) surface whose entire premise
// is a single daily action.

import type { ArchetypeSurface } from "../../onboarding/archetypeSurface.ts";

export type HomeSectionKey =
  | "hero"
  | "stats"
  | "roi"
  | "grid"
  | "signals"
  | "activity";

/** Canonical prototype order (also the honest default). The ROI
 *  block sits directly under the stat strip — doc §4.1 makes ROI the
 *  primary metric; it self-gates (sample-size warning under 20
 *  settled slips), so it is invariant across archetypes. */
export const DEFAULT_HOME_ORDER: readonly HomeSectionKey[] = [
  "hero",
  "stats",
  "roi",
  "grid",
  "signals",
  "activity",
] as const;

/**
 * Resolve the Home section order for a surface.
 *
 * - minimal complexity (Casual) ⇒ drop "signals": one daily action.
 * - signals/powerball-led (Value Hunter, Accumulator, Analyst) ⇒
 *   "signals" rises above the Action/Goal grid.
 * - sika/daily-led & the neutral default ⇒ canonical prototype order.
 */
export function orderHomeSections(
  surface: ArchetypeSurface,
): HomeSectionKey[] {
  if (surface.complexity === "minimal") {
    return ["hero", "stats", "roi", "grid", "activity"];
  }
  const signalsLed =
    surface.primaryModule === "signals" ||
    surface.primaryModule === "powerball";
  if (signalsLed) {
    return ["hero", "stats", "roi", "signals", "grid", "activity"];
  }
  return [...DEFAULT_HOME_ORDER];
}
